import { getMartData } from "api/ApiCalls"
import { applyFetcherOptions } from "helpers/martHelpers"
import { type MartFetcherOptions, type MarketSector } from "types/ApiTypes"
import type { IServerSideGetRowsRequest } from "@tokenterminal/ui/DataTable"

const DATASET_TABLE = `trending_contracts`

const DEFAULT_SSRM_REQUEST: IServerSideGetRowsRequest = {
  startRow: 0,
  endRow: 100,
  rowGroupCols: [],
  valueCols: [],
  pivotCols: [],
  pivotMode: false,
  groupKeys: [],
  filterModel: {},
  sortModel: [{ sort: "desc", colId: "gas_used_30d_sum" }],
}

type TrendingAccountsData = {
  chain_dbt_tag: string | null
  chain_id: string | null
  chain_name: string | null
  address: string | null
  title: string | null
  project_name: string | null
  project_dbt_tag: string | null
  is_mev_bot: boolean | null
  is_erc20: boolean | null
  is_erc721: boolean | null
  is_erc1155: boolean | null
  is_smart_contract: boolean | null
  is_eoa: boolean | null
  deployment_date:
    | {
        value: string
      }
    | string
    | null
  market_sectors: Array<MarketSector>
  market_sector_name: string | null
  market_sector_id: string | null
  avg_gas_per_tx_usd_1d: number | null
  avg_gas_per_tx_usd_7d: number | null
  avg_gas_per_tx_usd_30d: number | null
  avg_gas_per_tx_usd_90d: number | null
  avg_gas_per_tx_usd_180d: number | null
  avg_gas_per_tx_usd_365d: number | null
  avg_gas_per_tx_usd_trend_1d: number | null
  avg_gas_per_tx_usd_trend_7d: number | null
  avg_gas_per_tx_usd_trend_30d: number | null
  avg_gas_per_tx_usd_trend_90d: number | null
  avg_gas_per_tx_usd_trend_180d: number | null
  avg_gas_per_tx_usd_trend_365d: number | null
  gas_used_usd_1d: number | null
  gas_used_usd_7d: number | null
  gas_used_usd_30d: number | null
  gas_used_usd_90d: number | null
  gas_used_usd_180d: number | null
  gas_used_usd_365d: number | null
  gas_used_usd_trend_1d: number | null
  gas_used_usd_trend_7d: number | null
  gas_used_usd_trend_30d: number | null
  gas_used_usd_trend_90d: number | null
  gas_used_usd_trend_180d: number | null
  gas_used_usd_trend_365d: number | null
  inflows_usd_1d: number | null
  inflows_usd_7d: number | null
  inflows_usd_30d: number | null
  inflows_usd_90d: number | null
  inflows_usd_180d: number | null
  inflows_usd_365d: number | null
  inflows_usd_trend_1d: number | null
  inflows_usd_trend_7d: number | null
  inflows_usd_trend_30d: number | null
  inflows_usd_trend_90d: number | null
  inflows_usd_trend_180d: number | null
  inflows_usd_trend_365d: number | null
  most_emitted_event_decoded_1d: number | null
  most_emitted_event_decoded_7d: number | null
  most_emitted_event_decoded_30d: number | null
  most_emitted_event_decoded_90d: number | null
  most_emitted_event_decoded_180d: number | null
  most_emitted_event_decoded_365d: number | null
  n_interacting_addresses_1d: number | null
  n_interacting_addresses_7d: number | null
  n_interacting_addresses_30d: number | null
  n_interacting_addresses_90d: number | null
  n_interacting_addresses_180d: number | null
  n_interacting_addresses_365d: number | null
  n_interacting_addresses_trend_1d: number | null
  n_interacting_addresses_trend_7d: number | null
  n_interacting_addresses_trend_30d: number | null
  n_interacting_addresses_trend_90d: number | null
  n_interacting_addresses_trend_180d: number | null
  n_interacting_addresses_trend_365d: number | null
  n_transactions_1d: number | null
  n_transactions_7d: number | null
  n_transactions_30d: number | null
  n_transactions_90d: number | null
  n_transactions_180d: number | null
  n_transactions_365d: number | null
  n_transactions_trend_1d: number | null
  n_transactions_trend_7d: number | null
  n_transactions_trend_30d: number | null
  n_transactions_trend_90d: number | null
  n_transactions_trend_180d: number | null
  n_transactions_trend_365d: number | null
  outflows_usd_1d: number | null
  outflows_usd_7d: number | null
  outflows_usd_30d: number | null
  outflows_usd_90d: number | null
  outflows_usd_180d: number | null
  outflows_usd_365d: number | null
  outflows_usd_trend_1d: number | null
  outflows_usd_trend_7d: number | null
  outflows_usd_trend_30d: number | null
  outflows_usd_trend_90d: number | null
  outflows_usd_trend_180d: number | null
  outflows_usd_trend_365d: number | null
  net_flows_usd_1d: number | null
  net_flows_usd_7d: number | null
  net_flows_usd_30d: number | null
  net_flows_usd_90d: number | null
  net_flows_usd_180d: number | null
  net_flows_usd_365d: number | null
  net_flows_usd_trend_1d: number | null
  net_flows_usd_trend_7d: number | null
  net_flows_usd_trend_30d: number | null
  net_flows_usd_trend_90d: number | null
  net_flows_usd_trend_180d: number | null
  net_flows_usd_trend_365d: number | null
  pct_gas_market_1d: number | null
  pct_gas_market_7d: number | null
  pct_gas_market_30d: number | null
  pct_gas_market_90d: number | null
  pct_gas_market_180d: number | null
  pct_gas_market_365d: number | null
  pct_gas_market_trend_1d: number | null
  pct_gas_market_trend_7d: number | null
  pct_gas_market_trend_30d: number | null
  pct_gas_market_trend_90d: number | null
  pct_gas_market_trend_180d: number | null
  pct_gas_market_trend_365d: number | null
}

// Used for export. there's a TODO to remove
export async function getTrendingContractsData<T = Array<TrendingAccountsData>>(
  body: IServerSideGetRowsRequest = DEFAULT_SSRM_REQUEST,
  opts?: MartFetcherOptions
) {
  if (opts) {
    applyFetcherOptions(body, opts)
  }

  const martData = await getMartData<T>(DATASET_TABLE, body)

  return martData.data
}
